import { useMemo } from 'react';
import {
  ObservableLevelsEnum,
  PartCategory,
  PartType,
  TaskStatus,
} from '../../data/enums';
import { EquipmentModel, EquipmentPartModel } from '../../data/types';
import { ProgressIndicatorService } from '../../services/progress-indicator.service';
import { useInspection } from '../useInspection';
import { inspectionDB } from '../../data/database.structure';
import progressIndicators from '../../data/local-files/progress-indicators';
import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import RichTextFieldHelpers from '../../helpers/richTextFieldHelpers';

const defaults = {
  summary: TaskStatus.notStarted,
  heelShroudsIndicator: undefined as TaskStatus | undefined,
  wearPlatesIndicator: undefined as TaskStatus | undefined,
  wearPlateGroupedIndicators: undefined as
    | WearPlateIndicatorsIndex[]
    | undefined,
};

interface WearPlateIndex {
  locationId: number;
  secondaryLocations: {
    secondaryLocationId: number;
    groups: {
      groupId: number;
      wearPlates: EquipmentPartModel[];
    }[]; // Array of groups
  }[]; // Array of secondary locations
}

interface WearPlateIndicatorsIndex {
  locationId: number;
  summary: TaskStatus;
  photos: TaskStatus;
  secondaryLocations: {
    secondaryLocationId: number;
    groups: {
      groupId: number;
      wearPlates: { id: number; status: TaskStatus }[];
    }[]; // Array of groups
  }[]; // Array of secondary locations
}

const useInspectionBucketWearKit = (
  inspectionId: string,
  bucket?: EquipmentModel,
) => {
  const { inspection } = useInspection(inspectionId);

  function buildWearPlateIndex(
    wearPlates: EquipmentPartModel[],
  ): WearPlateIndex[] {
    // locId -> secId -> groupId -> plates[]
    const locMap = new Map<
      number,
      Map<number, Map<number, EquipmentPartModel[]>>
    >();

    const toNum = (v: unknown, fallback = -1): number => {
      if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    for (const wp of wearPlates) {
      const locId = toNum((wp as any).locationId);
      const secId = toNum((wp as any).secondaryLocationId);
      const groupId = toNum((wp as any).group, 0);

      let secMap = locMap.get(locId);
      if (!secMap) locMap.set(locId, (secMap = new Map()));

      let groupMap = secMap.get(secId);
      if (!groupMap) secMap.set(secId, (groupMap = new Map()));

      const arr = groupMap.get(groupId);
      if (arr) {
        arr.push(wp);
      } else {
        groupMap.set(groupId, [wp]);
      }
    }

    // Convert Maps -> arrays (optionally sort for stable output)
    const result: WearPlateIndex[] = Array.from(locMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([locationId, secMap]) => ({
        locationId,
        secondaryLocations: Array.from(secMap.entries())
          .sort(([a], [b]) => a - b)
          .map(([secondaryLocationId, groupMap]) => ({
            secondaryLocationId,
            groups: Array.from(groupMap.entries())
              .sort(([a], [b]) => a - b)
              .map(([groupId, wearPlates]) => ({
                groupId,
                wearPlates,
              })),
          })),
      }));

    return result;
  }

  const indicators = useMemo(() => {
    if (!inspection || !bucket) return defaults;

    const service = new ProgressIndicatorService(
      inspectionDB,
      inspection.inspection,
    );

    // Summary
    const summaryIndicator = service.processScreen({
      screen:
        progressIndicators.Root.Bucket.children.WearKit.children.WearPlates
          .children.PrimaryLocation,
      entityExternalId: bucket.externalId,
      dataLevel: ObservableLevelsEnum.equipment,
      assetExternalId: inspection.asset?.externalId,
    });

    const heelShrouds = bucket.equipmentParts.filter(
      (part) => part.part.partType === PartType.HeelShroud,
    );

    var heelShroudsIndicator: TaskStatus | undefined = undefined;

    if (heelShrouds.length > 0) {
      const indicators = heelShrouds.map((heelShroud) => {
        return service.processScreen({
          screen:
            progressIndicators.Root.Bucket.children.WearKit.children
              .HeelShrouds,
          assetExternalId: inspection.asset?.externalId,
          dataLevel: ObservableLevelsEnum.equipmentPart,
          entityExternalId: heelShroud.externalId,
          observationsCount: 2,
        });
      });

      const baseNotStarted = indicators.every(
        (status) => status === TaskStatus.notStarted,
      );
      if (baseNotStarted) {
        heelShroudsIndicator = TaskStatus.notStarted;
      } else if (
        indicators.every((status) => status === TaskStatus.completed)
      ) {
        heelShroudsIndicator = TaskStatus.completed;
      } else if (
        indicators.some((status) => status === TaskStatus.inProgress)
      ) {
        heelShroudsIndicator = TaskStatus.inProgress;
      }
    } else {
      heelShroudsIndicator = undefined;
    }

    // Wear Plates
    const wearPlates = bucket.equipmentParts.filter(
      (part) => part.part.partType === PartType.WearPlate,
    );
    var wearPlatesIndicator: TaskStatus | undefined = undefined;
    var wearPlatesIndicators: WearPlateIndicatorsIndex[] | undefined =
      undefined;
    if (wearPlates.length > 0) {
      const grouped = buildWearPlateIndex(wearPlates);

      // Get Indicators for each group
      wearPlatesIndicators = grouped.map(
        ({ locationId, secondaryLocations }) => ({
          locationId,
          secondaryLocations: secondaryLocations.map(
            ({ secondaryLocationId, groups }) => ({
              secondaryLocationId,
              groups: groups.map(({ groupId, wearPlates }) => ({
                groupId,
                wearPlates: wearPlates.map((wearPlate) => ({
                  id: wearPlate.externalId,
                  status: service.processScreen({
                    screen:
                      progressIndicators.Root.Bucket.children.WearKit.children
                        .WearPlates,
                    assetExternalId: inspection.asset?.externalId,
                    dataLevel: ObservableLevelsEnum.equipmentPart,
                    entityExternalId: wearPlate.externalId,
                    observationsCount: 2,
                  }),
                })),
              })),
            }),
          ),
        }),
      ) as unknown as WearPlateIndicatorsIndex[];

      // Summary Screens - do by hand to be quick
      const primaryLocationIds = grouped.map((group) => group.locationId);

      const primaryLocationSummaryCommentsObservation = (
        inspection.inspection.inspectionCategoryTypes[0]?.observations ?? []
      ).find(
        (obs) =>
          obs.dataLevelId === ObservableLevelsEnum.equipment &&
          obs.entityExternalId === bucket.externalId &&
          obs.observableItem.identifier ===
            ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
              .WearPlates.Location.Comments,
      );

      let primaryLocationCommentsIndicatorMap: {
        locationId: number;
        status: TaskStatus;
      }[] = [];

      if (primaryLocationSummaryCommentsObservation) {
        const primaryLocationCommentsMap = RichTextFieldHelpers.parseSummaries(
          primaryLocationSummaryCommentsObservation?.value ?? '',
        );
        primaryLocationCommentsIndicatorMap = Object.entries(
          primaryLocationCommentsMap,
        ).map((m) => {
          const comment = m[1];
          if (
            primaryLocationIds.includes(Number(m[0])) &&
            comment.trim() !== ''
          ) {
            return { locationId: Number(m[0]), status: TaskStatus.completed };
          } else {
            return { locationId: Number(m[0]), status: TaskStatus.notStarted };
          }
        });

        // Add Primary Comments to the wearplates indicator index at the first property level
        wearPlatesIndicators = wearPlatesIndicators.map((wpIndicator) => ({
          ...wpIndicator,
          summary:
            primaryLocationCommentsIndicatorMap.find(
              (m) => m.locationId === wpIndicator.locationId,
            )?.status ?? TaskStatus.notStarted,
        }));
      }

      const primaryLocationPhotoObservations = (
        inspection.inspection.inspectionCategoryTypes[0]?.observations ?? []
      ).filter(
        (obs) =>
          obs.dataLevelId === ObservableLevelsEnum.equipment &&
          obs.entityExternalId === bucket.externalId &&
          obs.observableItem.identifier ===
            ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
              .WearPlates.Location.Photo,
      );

      const primaryLocationPhotoIndicatorMap = primaryLocationIds.map(
        (locationId) => {
          const observations = primaryLocationPhotoObservations.filter(
            (obs) => {
              const photos = obs.value.startsWith('__' + locationId + '__');
              return photos;
            },
          );

          let wearPlateIndicatorIndex = wearPlatesIndicators?.findIndex(
            (wp) => wp.locationId === locationId,
          );
          if (
            wearPlateIndicatorIndex !== undefined &&
            wearPlateIndicatorIndex !== -1 &&
            wearPlatesIndicators
          ) {
            wearPlatesIndicators[wearPlateIndicatorIndex].photos =
              observations.length > 0
                ? TaskStatus.completed
                : TaskStatus.notStarted;
          }

          return {
            locationId,
            status:
              observations.length > 0
                ? TaskStatus.completed
                : TaskStatus.notStarted,
          };
        },
      );

      const indicators = wearPlatesIndicators.map((ind) => {
        const collection = [
          ind.summary,
          ...ind.secondaryLocations.map((sl) =>
            sl.groups.map((g) => g.wearPlates.map((wp) => wp.status)),
          ),
          ...primaryLocationPhotoIndicatorMap.map((m) => m.status),
        ];
        if (collection.every((item) => item === TaskStatus.completed)) {
          return TaskStatus.completed;
        } else if (
          collection.some(
            (item) =>
              item === TaskStatus.inProgress || item === TaskStatus.completed,
          )
        ) {
          return TaskStatus.inProgress;
        } else {
          return TaskStatus.notStarted;
        }
      });

      const baseNotStarted =
        indicators.every((status) => status === TaskStatus.notStarted) &&
        primaryLocationCommentsIndicatorMap.every(
          (m) => m.status === TaskStatus.notStarted,
        );
      if (baseNotStarted) {
        wearPlatesIndicator = TaskStatus.notStarted;
      } else if (
        indicators.every((status) => status === TaskStatus.completed) &&
        primaryLocationCommentsIndicatorMap.every(
          (m) => m.status === TaskStatus.completed,
        )
      ) {
        wearPlatesIndicator = TaskStatus.completed;
      } else if (
        indicators.some(
          (status) =>
            status === TaskStatus.inProgress || status === TaskStatus.completed,
        ) ||
        primaryLocationCommentsIndicatorMap.some(
          (m) =>
            m.status === TaskStatus.inProgress ||
            m.status === TaskStatus.completed,
        )
      ) {
        wearPlatesIndicator = TaskStatus.inProgress;
      }
    } else {
      wearPlatesIndicator = undefined;
    }

    return {
      summary: summaryIndicator,
      heelShroudsIndicator: heelShroudsIndicator,
      wearPlatesIndicator: wearPlatesIndicator,
      wearPlateGroupedIndicators:
        wearPlates.length > 0 ? wearPlatesIndicators : undefined,
    };
  }, [inspectionId, bucket, inspection]);

  // Optional: expose readiness so the UI can show a skeleton until both are present
  const isReady = useMemo(() => {
    return !!inspectionId && !!bucket && !!inspection;
  }, [inspectionId, bucket, inspection]);

  return { ...indicators, isReady };
};

export default useInspectionBucketWearKit;
